import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Scrolls to the element matching the current URL hash (e.g.
// /:companyId/settings/bookkeeping#mallar -> the "Bokföringsmallar" card) and
// briefly highlights it. Because each settings section lazy-loads its data on
// first visit, the target element may not exist yet when the hash changes — so
// this retry-polls for it for a few seconds before giving up. Pass the active
// section slug (and any other render-affecting values) as `deps` so it re-runs
// when the rendered content changes.
export default function useHashScroll(deps = []) {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timer;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        // scrollIntoView is absent in some test environments (jsdom).
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        el.classList.add('settings-anchor-flash');
        timer = setTimeout(() => el.classList.remove('settings-anchor-flash'), 1600);
        return;
      }
      // Element not rendered yet (section data still loading) — retry for ~3s.
      if (attempts++ < 40) {
        timer = setTimeout(tryScroll, 75);
      }
    };

    tryScroll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, ...deps]);
}
